//@ts-nocheck
import type { AxiosRequestConfig } from 'axios'
import { AxiosError } from 'axios'
import { getSession } from 'next-auth/react'
import { httpClient } from './httpClient'
import { getClientSessionWithRetry } from './session'
import {
  clearCachedAccessToken,
  getCachedAccessToken,
  getCachedAccessTokenSource,
  getLegacyStoredAccessToken,
  setCachedAccessToken,
  syncLegacyStoredTokens,
} from './tokenCache'

export type Response<T, E = any> =
  | T
  | {
      error: string
      statusCode: number
      data?: E
    }

const toUnknownResponse = <E = unknown>(error: unknown): {
  error: string
  statusCode: number
  data?: E
} => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    'error' in error
  ) {
    return error as {
      error: string
      statusCode: number
      data?: E
    }
  }

  if (error instanceof Error) {
    return {
      error: error.message || 'Unknown error',
      statusCode: 500,
    }
  }

  if (typeof error === 'string') {
    return {
      error,
      statusCode: 500,
    }
  }

  return {
    error: 'Internal Server Error',
    statusCode: 500,
  }
}

// SSR token loader
export async function getServerAccessToken() {
  try {
    const session = await getSession()
    const accessToken = session?.access_token

    return accessToken ?? null
  } catch (e) {
    return null
  }
}

// CSR token loader
let pendingPromise: Promise<string | null> | null = null

export async function getClientAccessToken() {
  const cachedToken = getCachedAccessToken()
  const cachedTokenSource = getCachedAccessTokenSource()

  // If token is already cached, return it
  if (cachedToken && cachedTokenSource !== 'legacy') return cachedToken

  if (cachedToken && cachedTokenSource === 'legacy') {
    const session = await getSession()

    if (session?.access_token) {
      syncLegacyStoredTokens(session.access_token, session?.refresh_token ?? null)
      setCachedAccessToken(session.access_token, (session as any)?.expiry ?? null)

      return session.access_token
    }

    return cachedToken
  }

  // If a fetch is already happening, wait for it
  if (pendingPromise) return pendingPromise

  // Otherwise, start a new fetch
  pendingPromise = (async () => {
    try {
      const session = await getClientSessionWithRetry()
      const accessToken = session?.access_token ?? getLegacyStoredAccessToken()

      if (session?.access_token) {
        syncLegacyStoredTokens(session.access_token, session?.refresh_token ?? null)
      } else if (session && !session?.access_token) {
        syncLegacyStoredTokens(null, null)
      }

      if (accessToken) {
        setCachedAccessToken(
          accessToken,
          (session as any)?.expiry ?? null,
          session?.access_token ? 'session' : 'legacy',
        )
      } else {
        clearCachedAccessToken()
      }

      return accessToken
    } catch (error) {
      clearCachedAccessToken()
      return null
    } finally {
      // Clear pending promise regardless of result
      pendingPromise = null
    }
  })()

  return pendingPromise
}

// UNIVERSAL loader
export async function getAccessToken() {
  if (typeof window === 'undefined') {
    return await getServerAccessToken()
    // SSR
  }
  // CSR
  return getClientAccessToken()
}

const getConfigWithAuthorization = async (
  additionalConfig: AxiosRequestConfig = {},
): Promise<AxiosRequestConfig> => {
  try {
    const accessToken = await getAccessToken() // SSR + CSR

    return {
      ...additionalConfig,
      headers: {
        ...additionalConfig?.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    }
  } catch (err) {
    console.error(err)

    return additionalConfig
  }
}

export const get = async <R>(
  slug: string,
  additionalConfig?: AxiosRequestConfig,
  withoutAuth?: boolean,
): Promise<Response<R>> => {
  try {
    if (withoutAuth) {
      const response = await httpClient.get(slug, additionalConfig)

      return response.data
    }
    const config = await getConfigWithAuthorization(additionalConfig)

    const response = await httpClient.get(slug, config)

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e.response

      return {
        error: error?.data?.message ?? error?.statusText ?? 'Error',
        statusCode: error?.status ?? 400,
      }
    }

    return toUnknownResponse(e)
  }
}

export const post = async <P, R = P, E = unknown>(
  slug: string,
  payload: P,
  additionalConfig?: AxiosRequestConfig,
  withoutAuth?: boolean,
): Promise<Response<R>> => {
  try {
    const config = withoutAuth
      ? additionalConfig
      : await getConfigWithAuthorization(additionalConfig)

    const response = await httpClient.post(slug, payload, config)

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e.response

      return {
        error: error?.data?.message ?? error?.statusText ?? 'Error',
        statusCode: error?.status ?? 400,
        data: error?.data as E,
      }
    }

    return toUnknownResponse<E>(e)
  }
}

export const put = async <P, R = P>(
  slug: string,
  payload: P,
  additionalConfig?: AxiosRequestConfig,
  withoutAuth?: boolean,
): Promise<Response<R>> => {
  try {
    const config = withoutAuth
      ? additionalConfig
      : await getConfigWithAuthorization(additionalConfig)

    const response = await httpClient.put(slug, payload, config)

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e.response

      return {
        error: error?.data?.message ?? error?.statusText ?? 'Error',
        statusCode: error?.status ?? 400,
      }
    }

    return toUnknownResponse(e)
  }
}

export const patch = async <P, R = P, E = unknown>(
  slug: string,
  payload: P,
  additionalConfig?: AxiosRequestConfig,
  withoutAuth?: boolean,
): Promise<Response<R>> => {
  try {
    const config = withoutAuth
      ? additionalConfig
      : await getConfigWithAuthorization(additionalConfig)

    const response = await httpClient.patch(slug, payload, config)

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e.response

      return {
        error: error?.data?.message ?? error?.statusText ?? 'Error',
        statusCode: error?.status ?? 400,
        data: error?.data as E,
      }
    }

    return toUnknownResponse<E>(e)
  }
}

export const DELETE = async <R>(
  slug: string,
  additionalConfig?: AxiosRequestConfig,
  withoutAuth?: boolean,
): Promise<Response<R>> => {
  try {
    const config = withoutAuth
      ? additionalConfig
      : await getConfigWithAuthorization(additionalConfig)

    const response = await httpClient.delete(slug, config)

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e.response

      return {
        error: error?.data?.message ?? error?.statusText ?? 'Error',
        statusCode: error?.status ?? 400,
      }
    }

    return toUnknownResponse(e)
  }
}
